import React, { useMemo } from 'react';

import { formatPrice } from 'utils/common';

import type { CartBilling } from 'domains/cart_billing';

type BillingSummaryProps = {
  billing: CartBilling | null;
  hideCheckoutTotals?: boolean;
};

function formatSummaryPrice(price?: number, atCost?: boolean) {
  if ((!price || price === 0) && !atCost) return '-';

  return formatPrice(price, undefined, atCost);
}

function BillingSummary({ billing, hideCheckoutTotals = false }: BillingSummaryProps) {
  const subtotal = useMemo(() => formatSummaryPrice(billing?.subtotal), [billing]);
  const shipping = useMemo(
    () => formatSummaryPrice(billing?.shipping_subtotal, billing?.shipping_at_cost),
    [billing]
  );
  const tax = useMemo(() => formatSummaryPrice(billing?.tax), [billing]);
  const total = useMemo(() => (billing ? formatPrice(billing.total) : '-'), [billing]);

  return (
    <table className="billing-summary">
      <tbody>
        <tr aria-label="cart subtotal">
          <th>Subtotal</th>
          <td>{subtotal}</td>
        </tr>

        {billing && billing.discount > 0 && (
          <tr className="discount-label" aria-label="cart discount">
            <th>Discount</th>
            <td>-{formatPrice(billing.discount)}</td>
          </tr>
        )}

        {!hideCheckoutTotals && (
          <React.Fragment>
            {billing && billing.handling_total > 0 && (
              <tr aria-label="crate fee">
                <th>Crate Fee</th>
                <td>{formatPrice(billing.handling_total)}</td>
              </tr>
            )}
            <tr aria-label="shipping cost">
              <th>Shipping</th>
              <td>{shipping}</td>
            </tr>
            {billing && billing.shipping_discount > 0 && (
              <tr aria-label="shipping discount">
                <th>Shipping Discount</th>
                <td>-{formatPrice(billing.shipping_discount)}</td>
              </tr>
            )}
            <tr aria-label="tax cost">
              <th>Tax</th>
              <td>{tax}</td>
            </tr>
          </React.Fragment>
        )}
      </tbody>

      {!hideCheckoutTotals && (
        <tfoot>
          <tr aria-label="total cost">
            <th>Total</th>
            <td>{total}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

export default BillingSummary;
